import type { HocuspocusProvider } from "@hocuspocus/provider";
import type { UserInfo, UserPresence } from "../types";

/**
 * Update this user's cursor position in the awareness protocol
 */
export function updateCursorPosition(
  provider: HocuspocusProvider,
  x: number,
  y: number,
): void {
  provider.setAwarenessField("cursor", { x, y });
}

/**
 * Clear this user's cursor (e.g. when mouse leaves the canvas)
 */
export function clearCursor(provider: HocuspocusProvider): void {
  provider.setAwarenessField("cursor", null);
}

/**
 * Update the selected frame for this user
 */
export function updateSelectedFrame(
  provider: HocuspocusProvider,
  frameId: string | null,
): void {
  provider.setAwarenessField("selectedFrameId", frameId);
}

/**
 * Get all remote users' presence states (excluding local user)
 */
export function getRemotePresences(
  provider: HocuspocusProvider,
): UserPresence[] {
  const awareness = provider.awareness;
  if (!awareness) return [];

  const localClientId = awareness.clientID;
  const presences: UserPresence[] = [];

  awareness.getStates().forEach((state, clientId) => {
    if (clientId === localClientId) return;

    const user = state.user as UserInfo | undefined;
    const cursor = state.cursor as { x: number; y: number } | null | undefined;
    const selectedFrameId = state.selectedFrameId as string | null | undefined;

    if (user && cursor) {
      presences.push({
        user,
        cursorX: cursor.x,
        cursorY: cursor.y,
        selectedFrameId: selectedFrameId ?? null,
      });
    }
  });

  return presences;
}

/**
 * Get all connected users (including local)
 */
export function getConnectedUsers(provider: HocuspocusProvider): UserInfo[] {
  const awareness = provider.awareness;
  if (!awareness) return [];

  const users: UserInfo[] = [];

  awareness.getStates().forEach((state) => {
    const user = state.user as UserInfo | undefined;
    if (user) {
      users.push(user);
    }
  });

  return users;
}

/**
 * Observe awareness changes (cursors moving, users joining/leaving)
 */
export function observePresence(
  provider: HocuspocusProvider,
  handler: (presences: UserPresence[], users: UserInfo[]) => void,
): () => void {
  const awareness = provider.awareness;
  if (!awareness) return () => {};

  const onUpdate = () => {
    handler(getRemotePresences(provider), getConnectedUsers(provider));
  };

  awareness.on("change", onUpdate);

  return () => {
    awareness.off("change", onUpdate);
  };
}
